/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './OrganizationChart';

const OrganizationChartManager: ComponentManagerFn<'OrganizationChart'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'OrganizationChart'>(platform, 'OrganizationChart'),
    type: 'OrganizationChart',
    name: 'Organization Chart',
    isChildAllowed: (page, item, childType) =>
      (['Box', 'Card'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      keyAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Primary Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'Choose an attribute that should contain the primary node value.',
      },
      parentAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Parent Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'Choose an attribute that should contain the parent node value.',
      },
      chartHeight: {
        label: 'Chart Height',
        type: 'number',
        max: 800,
        min: 0,
        step: 40,
        helpText: 'Provide a number to set height of the chart.',
      },
      chartWidth: {
        label: 'Chart Width',
        type: 'number',
        max: 1000,
        min: 0,
        step: 40,
        helpText: 'Provide width for the chart.',
      },
      nodeWidth: {
        label: 'Node Width',
        type: 'number',
        max: 1000,
        min: 0,
        step: 40,
        helpText:
          'Provide a numeric value to set the width of the node in pixels.',
      },
      margin: {
        label: 'Node margin',
        type: 'string',
        helpText:
          'Specify a margin value (in pixels) to apply spacing around the node.',
      },
      pinRootNode: {
        label: 'Pin Root Node',
        type: 'boolean',
        helpText:
          'When enabled, the root node stays visible while you scroll horizontally through the chart.',
      },
      groupLeafNodes: {
        label: 'Group Leaf Nodes',
        type: 'boolean',
        helpText:
          'When enabled, nodes in the last layer (leaf nodes) are grouped together with a wrapping layout.',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'keyAttribute':
        case 'parentAttribute':
          return !!itemData.dsalias;
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    category: 'Forms',
    icon: { iconName: 'database' },
    description:
      'Organization Chart is a component used to view the employees working in the organization in hierarchy!',
  };
};

export default OrganizationChartManager;
